import { create } from 'zustand';
import { persist, createJSONStorage } from 'zustand/middleware';

export type HighlightLayer = 'citation' | 'tool' | 'blast';

export interface HighlightsState {
  citation: string[];
  tool: string[];
  blast: string[];
  visible: boolean;
}

export const LEFT_PANEL_MIN_WIDTH = 240;
export const LEFT_PANEL_MAX_WIDTH = 640;
export const LEFT_PANEL_DEFAULT_WIDTH = 288;

export function clampLeftPanelWidth(value: number): number {
  if (!Number.isFinite(value)) return LEFT_PANEL_DEFAULT_WIDTH;
  if (value < LEFT_PANEL_MIN_WIDTH) return LEFT_PANEL_MIN_WIDTH;
  if (value > LEFT_PANEL_MAX_WIDTH) return LEFT_PANEL_MAX_WIDTH;
  return Math.round(value);
}

interface UiState {
  selectedRepo: string | null;
  activeJobId: string | null;
  leftPanelOpen: boolean;
  leftPanelWidth: number;
  rightPanelOpen: boolean;
  selectedNodeId: string | null;
  focusDepth: 1 | 2 | 3;
  highlights: HighlightsState;
  showFileLabels: boolean;
  setSelectedRepo: (slug: string | null) => void;
  setActiveJobId: (jobId: string | null) => void;
  setLeftPanelOpen: (open: boolean) => void;
  setLeftPanelWidth: (width: number) => void;
  setRightPanelOpen: (open: boolean) => void;
  setSelectedNodeId: (id: string | null) => void;
  setFocusDepth: (depth: 1 | 2 | 3) => void;
  setHighlights: (layer: HighlightLayer, ids: string[]) => void;
  clearHighlights: (layer?: HighlightLayer) => void;
  toggleHighlightsVisible: () => void;
  setShowFileLabels: (value: boolean) => void;
}

const EMPTY_HIGHLIGHTS: HighlightsState = {
  citation: [],
  tool: [],
  blast: [],
  visible: true,
};

export const useUiStore = create<UiState>()(
  persist(
    (set) => ({
      selectedRepo: null,
      activeJobId: null,
      leftPanelOpen: false,
      leftPanelWidth: LEFT_PANEL_DEFAULT_WIDTH,
      rightPanelOpen: false,
      selectedNodeId: null,
      focusDepth: 1,
      highlights: EMPTY_HIGHLIGHTS,
      showFileLabels: false,
      setSelectedRepo: (slug) => set({ selectedRepo: slug }),
      setActiveJobId: (jobId) => set({ activeJobId: jobId }),
      setLeftPanelOpen: (open) => set({ leftPanelOpen: open }),
      setLeftPanelWidth: (width) => set({ leftPanelWidth: clampLeftPanelWidth(width) }),
      setRightPanelOpen: (open) => set({ rightPanelOpen: open }),
      setSelectedNodeId: (id) => set({ selectedNodeId: id }),
      setFocusDepth: (depth) => set({ focusDepth: depth }),
      setHighlights: (layer, ids) =>
        set((state) => ({ highlights: { ...state.highlights, [layer]: ids } })),
      clearHighlights: (layer) =>
        set((state) =>
          layer
            ? { highlights: { ...state.highlights, [layer]: [] } }
            : {
                highlights: {
                  ...state.highlights,
                  citation: [],
                  tool: [],
                  blast: [],
                },
              },
        ),
      toggleHighlightsVisible: () =>
        set((state) => ({
          highlights: { ...state.highlights, visible: !state.highlights.visible },
        })),
      setShowFileLabels: (value) => set({ showFileLabels: value }),
    }),
    {
      name: 'wt-codegraph.ui-store',
      storage: createJSONStorage(() => localStorage),
      partialize: (state) => ({
        leftPanelWidth: state.leftPanelWidth,
        showFileLabels: state.showFileLabels,
      }),
    },
  ),
);
